import { Component, OnInit, ViewChild } from '@angular/core';

// Model
import { FreeTrialSignup, UserInfo, SchoolInfo, PaymentInfo } from '../../model/free-trial';

// Services
import { BsModalService } from 'ngx-bootstrap';
import { DataService } from '../../services/data.service';
import { FreeTrialService } from '../../services/free-trial.service';
import { AnalyticsService } from '../../services/analytics.service';
import { AuthService } from '../../services/auth.service';
import { AppService } from '../../services/app.service';

// Components
import { RegisterUserComponent } from '../register-user/register-user.component';
import { RegisterSchoolComponent } from '../register-school/register-school.component';
import { RegisterPaymentComponent } from '../register-payment/register-payment.component';

// Moment
import * as moment from 'moment';

//npm modules
import * as CryptoJS from 'crypto-js';

@Component({
	selector: 'app-free-trial',
	templateUrl: './free-trial.component.html',
	styleUrls: ['./free-trial.component.scss']
})
export class FreeTrialComponent implements OnInit {

	doneClicked: boolean = false;
	isRegistering: boolean = false;
	isResendingLink: boolean= false;
	errorMessage: string = "";

	captchaValue: string = null;
	uidHash: string;

	private signup: FreeTrialSignup = null;

	steps = [
		{done: false, name: 'register'},
		{done: false, name: 'verify'},
		{done: false, name: 'school'},
		{done: false, name: 'review'},
	];

	stepIndex: number = 0;

	freeTrialStart: Date = new Date();
	freeTrialEnd: Date = moment().add(1,'M').toDate();

	minimizeSchoolInfo: boolean = false;
	minimizeFAQs: boolean = true;

	@ViewChild('RegisterUser') RegisterUser: RegisterUserComponent;
	@ViewChild('RegisterSchool') RegisterSchool: RegisterSchoolComponent;
	@ViewChild('RegisterPayment') RegisterPayment: RegisterPaymentComponent;
	constructor(
		private dataSvc: DataService,
		private modalSvc: BsModalService,
		private freeTrialSvc: FreeTrialService,
		private analyticsSvc: AnalyticsService,
		private authSvc: AuthService,
		private appSvc: AppService
	) { }

	// Return Progress for progress-bar
	get progress() {
		let steps = this.steps.filter(s => { return !!s.done });

		return (steps.length / 3);
	}

	get currentStep() { return this.steps[this.stepIndex] }

	// return name for Payment Registration to pre populate names
	get paymentAutofills() {
		if (!this.signup)
			return {fName:"",lName: ""};
		
		if (!this.signup.userInfo)
			return {fName:"",lName: ""};

		return {
			fName: this.signup.userInfo.fName,
			lName: this.signup.userInfo.lName
		}
	}

	get existingSchoolAlias() {
		if (!this.existingSchoolInfo)
			return null;

		return this.existingSchoolInfo.alias;
	}

	get existingSchoolInfo() {return this.signup.schoolInfo || null;}
	get existingPaymentInfo() {return this.signup.paymentInfo || null;}
	get isMobile(): boolean {return window.innerWidth < 500;}
	get verifyBodyStyle() { 
		if (this.isMobile)
			return { "max-width": '100%'}

		return { "max-width": '85%'}
	}

	ngOnInit() {
		this.signup = new FreeTrialSignup(null, null, null, null);

		// On init 
		// 		Check user's progress
		this.getCurrentSession();
	}

	async getCurrentSession() {
		let currentSession: {
			userInfo?: UserInfo, 
			schoolInfo?: SchoolInfo, 
			leadID?: string, 
			newlyVerified?: boolean
		} = this.dataSvc.getItemFromSessionStorage("currentSession");

		if (!!currentSession){
			let userExists: boolean = !!currentSession.userInfo;
			let schoolExists: boolean = !!currentSession.schoolInfo;
			let leadExists: boolean = !!currentSession.leadID;

			if (userExists) {
				this.signup.userInfo = new UserInfo(
					currentSession.userInfo.fName, 
					currentSession.userInfo.lName, null, 
					currentSession.userInfo.email, 
					currentSession.userInfo.uid,
					currentSession.userInfo.emailVerified
				);

				// up index so user can start on the right step
				this.activateNextStep();

				// If user's email has been verified, activate next step
				if (currentSession.userInfo.emailVerified) {
					this.activateNextStep();
				}
			}

			if (leadExists) 
				this.signup.leadID = currentSession.leadID;

			if (schoolExists) {
				this.signup.schoolInfo = new SchoolInfo(
					currentSession.schoolInfo.schoolName,
					currentSession.schoolInfo.phoneNumber,
					currentSession.schoolInfo.address,
					currentSession.schoolInfo.city,
					currentSession.schoolInfo.state,
					currentSession.schoolInfo.zip, 
					currentSession.schoolInfo.alias,
					currentSession.schoolInfo.accountNumber || null,
					currentSession.schoolInfo.generatedAliases
				);

				// up index so user can start on the right step
				this.activateNextStep();
			}

			// Refresh session
			this.updateSession(currentSession.userInfo || null,currentSession.schoolInfo || null,currentSession.leadID || null )
		}
	}

	async btnNextStep() {
		// Prevent multiple clicks
		if (this.isRegistering) 
			return; 

		this.isRegistering = true;

		try {
			switch(this.currentStep.name) {
				case 'register': {
					await this.btnRegisterUser();
					this.analyticsSvc.trackEventGTag({category: 'Free Trial', action: 'User Account Created'});
					break;
				}
				case 'verify': {
					await this.btnConfirmEmail();
					this.analyticsSvc.trackEventGTag({category: 'Free Trial', action: 'Email Verified'})
					break;
				}
				case 'school': {
					await this.btnRegisterSchool();
					this.analyticsSvc.trackEventGTag({category: 'Free Trial', action: 'School Created'});
					break;
				}
				case 'review': {
					await this.btnRegisterAll();
					this.analyticsSvc.trackEventGTag({category: 'Free Trial', action: 'Completed'});
					break;
				}
			}

			this.activateNextStep();
			this.isRegistering = false;
		}
		catch(e) {
			this.isRegistering = false;
			this.manageError(e.message);
		}

		// Scroll to top if on mobile
		if (this.isMobile) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}

	async btnRegisterUser(): Promise<any> {
		try {
			if (!this.captchaValue) {
				throw new Error('The captcha is required.')
			}

			let userRegistration: {userInfo: UserInfo, signedUp?: boolean} = await this.RegisterUser.btnDone();
			let userInfo = userRegistration ? userRegistration.userInfo: null;
			if (!userInfo) {
				throw new Error('form error');
			}

			// User's signed up
			if (userRegistration.signedUp) {
				this.authSvc.currentUser().sendEmailVerification();
			}
			// User's logged back in, and has been verified. Skip verification step
			else if (userInfo.emailVerified) {
				this.activateNextStep();

				// Returning user who Logged In
				this.queryLeadAsyncronously(userInfo);
			}
			// User's logged back in but hasn't verified email
			else {
				this.authSvc.currentUser().sendEmailVerification();
			}

			// Save user info locally and on local storage
			this.signup.userInfo = userInfo;
			this.updateSession(userInfo, null, null);

			return; 
		}
		catch(e) {
			console.log(e.message);
			throw e;
		}
	}

	async btnConfirmEmail(): Promise<any> {
		try {
			let currentUser: firebase.User = this.authSvc.currentUser();
			if (!currentUser) {
				throw new Error('Expired credentials. Please log in again.');
			}

			// Reload user to see emailVerified value
			await currentUser.reload();

			if (!currentUser.emailVerified) {
				throw new Error('Your email hasn\'t been verified yet!')
			}

			let userInfo: UserInfo = this.signup.userInfo;

			// Create lead in dynamics & save LEADID asyncronously
			this.createLeadAsyncronously(userInfo).then(() => {
				const { leadID } = this.signup;

				// Schedule Finish-Signup email that sends if daycare director fails to complete the signup process
				this.freeTrialSvc.createFinishSignupEmail(userInfo.fName, userInfo.uid, new Date(), userInfo.email, leadID).catch((err) => { console.log(err) });
			}).catch((err) => {
				console.log(err);
			});

			// Update user info locally and in session
			this.signup.userInfo.emailVerified = true;
			userInfo.emailVerified = true;
			this.updateSession(userInfo, null, null)
			return;
		} catch (e) {
			throw e;
		}
	}

	async btnRegisterSchool(): Promise<any> {
		try {
			let schoolInfo: SchoolInfo = await this.RegisterSchool.btnDone();
			if (!schoolInfo) {
				throw new Error('form error');
			}

			let leadID: string = this.signup.leadID;

			this.freeTrialSvc.updateLeadWithDaycare(leadID, schoolInfo);

			// Save school info locally and on local storage
			this.signup.schoolInfo = schoolInfo
			this.updateSession(null, schoolInfo, null);

			return;
		}
		catch(e) {
			if (e.message && e.message !== 'form error' && e.message !== 'Invalid phone number.')
				throw new Error('Failed to create your school, please try again');

			throw e;
		}
	}

	async btnRegisterAll(): Promise<any> {
		try {
			let schoolInfo: SchoolInfo = await this.RegisterSchool.btnDone();
			if (!schoolInfo) {
				throw new Error('form error');
			}

			let userInfo: UserInfo = this.signup.userInfo;
			let leadID: string = this.signup.leadID;
			let cpFormKeys = {key: "9ClYb1CmoYruWdFyToabCCcQLTO1k3UCOO45jc9X"};
			this.uidHash = CryptoJS.AES.encrypt(this.signup.userInfo ? this.signup.userInfo.uid : "", cpFormKeys.key).toString();


			// Create daycare document via API
			let res: {accountNumber: string, startDate: any } = await this.freeTrialSvc.saveFreeTrialSchool(schoolInfo, userInfo);

			// // Send free trial signup email
			this.freeTrialSvc.sendFreeTrialConfirmation(schoolInfo.schoolName);
			// // Call API to create follow up emails for this customer
			this.freeTrialSvc.createFollowUpEmails(schoolInfo.schoolName, res.startDate, userInfo.email, leadID);
			// // Cancel 'Finish Signup' email bc daycare director HAS signed up.
			this.freeTrialSvc.cancelFinishSignupEmail(userInfo.uid);

			this.freeTrialSvc.markLeadFulfilled(leadID, schoolInfo);

			this.dataSvc.clearSessionStorage();
			
			return;
		}
		catch(e) {
			throw e;
		}
	}

	// Allows user to reenter their email and user information
	btnChangeEmail() {
		try {
			this.signup = new FreeTrialSignup(null, null, null, null);
			this.dataSvc.clearSessionStorage();

			this.previousStep();
		} catch (e) {
			console.log(e);
		}
	}

	// Determine if signup is completed
	isSignupComplete(): boolean {
		return !this.steps.some(s => {
			return s.done === false
		})
	}

	// Runs when user needs to resent email verification link.
	// Uses firebase.auth to send email verification
	async btnResendLink() {
		try {
			this.isResendingLink = true;
			// Resend verification email
			await this.authSvc.currentUser().sendEmailVerification()

			this.isResendingLink = false;
			this.appSvc.showAlert("Verification email sent successfully.","success-custom", true, 2000);
		} catch (e) {
			this.isResendingLink = false;

			if (e.message === "TOO_MANY_ATTEMPTS_TRY_LATER") {
				this.appSvc.showAlert("Too many attempts. Please try again later.","danger-custom", true, 2000);

			}

			this.appSvc.showAlert("Failed to send verification email. Please try again.","danger-custom", true, 2000);
		}
	}

	btnLink() {
		let schoolInfo = this.signup.schoolInfo;
		window.open(schoolInfo.getURL() + "/?uidHash=" + (this.uidHash || ""), '_blank')
	}

	// get step from the name
	private getStep(stepName: string) {
		return this.steps.find(s => {
			return s.name === stepName;
		})
	}

	// Determine what text to show in the card-footer button
	getStepText() {
		let activeStep = this.steps[this.stepIndex];

		if (activeStep.name === 'review') {
			return 'Start Free Trial';
		}

		return 'Next Step'
	}

	// Change step index to start next step
	private activateNextStep() {
		this.steps[this.stepIndex].done = true;
		this.stepIndex ++;
		this.errorMessage = "";
	}

	// Change step index to previous step. Also mark done as false
	private previousStep() {
		this.steps[this.stepIndex -1].done = false;
		this.stepIndex --;
		this.errorMessage = ""
	}

	// Show error alert on page
	private manageError(errorMessage: string) {
		if (!errorMessage || errorMessage === 'form error')
			return;
		
		this.errorMessage = errorMessage;
	}

	// Called after a user's information is captured
	// Calls firebase function to create a lead in dynamics
	// Updates leadID locally and in session once retrieved
	private async createLeadAsyncronously(userInfo: UserInfo) {
		let leadID: string = "";

		try {
			// Call API to create lead in dynamics
			leadID = await this.freeTrialSvc.createLeadFromUser(userInfo);

			// Update leadID in local object
			this.signup.leadID = leadID;

		} catch (error) {
			console.log(error);
		}

		// update session storage without overwriting userInfo and schoolInfo
		this.updateSession(null,null,leadID);
	}

	// Called after the user logs in after returning to complete signup
	// This function only becomes useful if the browser local storage has been wiped
	// Needed because we need to get the leadid now that it's not saved in browser
	private async queryLeadAsyncronously(userInfo: UserInfo) {
		let leadID: string = "";

		try {
			// Call API to query lead by email in dynamics
			leadID = await this.freeTrialSvc.queryLeadFromUser(userInfo);

			// Update leadID in local object
			this.signup.leadID = leadID;

		} catch (error) {
			console.error("queryLeadAsyncronously error");
			console.log(error);
		}

		// update session storage without overwriting userInfo and schoolInfo
		this.updateSession(null,null,leadID);
	}

	// Update Session Storage
	// Pass in values you want saved
	updateSession(userInfo?: UserInfo, schoolInfo?: SchoolInfo, leadID?: string) {
		// Get existing session
		try {
			let currentSession: {userInfo?: UserInfo, schoolInfo?: SchoolInfo, leadID?: string} | null = this.dataSvc.getItemFromSessionStorage("currentSession");

			// On first entry, current session will be null
			if (currentSession === null)
				currentSession = {};

			let newSession = {
				userInfo: this.getSessionVariable(userInfo, currentSession.userInfo),
				schoolInfo: this.getSessionVariable(schoolInfo, currentSession.schoolInfo),
				leadID: this.getSessionVariable(leadID, currentSession.leadID)
			};

			// Save new session without overwriting things we didn't want to
			this.dataSvc.saveItemToSessionStorage('currentSession', newSession);
		}
		catch(e) {
			console.error(e);
		}
	}

	// how we prioritize the existing session and paramters for setting session data
	private getSessionVariable(param, session) {
		if (param)
			return param;
		if (session)
			return session;
		return null;
	}

	// Return FontAwesome icon
	getIconClass(stepName: string) {
		let step = this.getStep(stepName);

		if (this.currentStep.name === 'register')
			return '';

		if (step.done)
			return 'fas fa-check-circle text-success larger-icon';
		return 'far fa-circle';
	}

	// Returns bootstrap css
	getBtnWidth() {
		// Make buttons full width on mobile
		if (this.isMobile) {
			return 'w-100'
		}

		// otherwise 50%
		return 'w-50'
	}
}
